import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { FILMS } from "../data/films";

const EASE = [0.22, 1, 0.36, 1];

// Fotogramas del catálogo que rotan en el marco del hero
const STILLS = FILMS.filter((f) => f.poster).map((f) => ({
    src: f.poster,
    titulo: f.titulo,
    anio: f.anio,
}));

// Línea con revelado enmascarado (masked line reveal)
// El padding inferior evita que la máscara corte las descendentes (la "j")
function MaskedLine({ children, delay, className }) {
    return (
        <span className={`-mb-[0.18em] block overflow-hidden pb-[0.18em] ${className || ""}`}>
            <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ delay, duration: 1, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero({ scrollTo }) {
    const ref = useRef(null);
    const [idx, setIdx] = useState(0);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    // Rotación de fotogramas cada 4.5 s
    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % STILLS.length), 4500);
        return () => clearInterval(t);
    }, []);

    return (
        <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
            <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:gap-8">
                {/* Columna de texto */}
                <motion.div style={{ opacity: fade }} className="flex flex-col justify-center lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="font-mono-jf text-xs uppercase tracking-[0.3em] text-[#6B1D2F]"
                        data-testid="hero-eyebrow"
                    >
                        Cine independiente mexicano
                    </motion.p>
                    <h1 className="mt-8 font-display leading-[1.02] tracking-tight text-[#141312]" data-testid="hero-wordmark">
                        <MaskedLine delay={0.35} className="text-[clamp(3.8rem,11vw,10rem)]">jaqueca</MaskedLine>
                        <MaskedLine delay={0.5} className="text-[clamp(3.8rem,11vw,10rem)] text-[#6B1D2F]">films</MaskedLine>
                    </h1>
                    <div className="mt-10 flex items-center gap-4">
                        <motion.span
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.8, duration: 0.7, ease: EASE }}
                            className="h-px w-14 origin-left bg-[#6B1D2F]"
                        />
                        <MaskedLine delay={0.75}>
                            <em className="font-display text-xl italic text-[#524E48] sm:text-2xl" data-testid="hero-tagline">
                                Todo por la ficción.
                            </em>
                        </MaskedLine>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.7, ease: EASE }}
                        className="mt-12 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={() => scrollTo("#peliculas")}
                            className="group inline-flex items-center gap-3 rounded-full bg-[#6B1D2F] px-7 py-3.5 font-mono-jf text-xs uppercase tracking-[0.2em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#8C273E]"
                            data-testid="hero-cta-peliculas"
                        >
                            Ver películas
                            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                        </button>
                        <button
                            onClick={() => scrollTo("#nosotros")}
                            className="font-mono-jf text-xs uppercase tracking-[0.2em] text-[#524E48] underline decoration-[#6B1D2F]/40 underline-offset-8 transition-colors duration-300 hover:text-[#6B1D2F]"
                            data-testid="hero-cta-nosotros"
                        >
                            La productora
                        </button>
                    </motion.div>
                </motion.div>

                {/* Marco cinematográfico con parallax */}
                <motion.div
                    initial={{ clipPath: "inset(100% 0 0 0)" }}
                    animate={{ clipPath: "inset(0% 0 0 0)" }}
                    transition={{ delay: 0.55, duration: 1.2, ease: EASE }}
                    className="relative lg:col-span-5"
                    data-testid="hero-image-frame"
                >
                    <div className="relative aspect-[2/1] overflow-hidden bg-[#141312]" data-testid="hero-slideshow">
                        <motion.div style={{ y: imgY }} className="absolute inset-0 h-[120%]">
                            <AnimatePresence mode="sync">
                                <motion.div
                                    key={STILLS[idx].src}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 1.4, ease: "easeInOut" }}
                                    className="absolute inset-0"
                                >
                                    {/* Fondo desenfocado del mismo fotograma (letterbox cinematográfico) */}
                                    <img
                                        src={STILLS[idx].src}
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-md"
                                    />
                                    {/* Fotograma completo, sin recorte */}
                                    <img
                                        src={STILLS[idx].src}
                                        alt={`Fotograma de la película ${STILLS[idx].titulo} (Jaqueca Films, ${STILLS[idx].anio})`}
                                        className="absolute inset-0 h-full w-full object-contain"
                                        loading="eager"
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                        <div className="absolute inset-0 bg-[#6B1D2F]/10 mix-blend-multiply" />
                    </div>
                    <div className="mt-3 flex items-center justify-between font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#736E65]">
                        <span data-testid="hero-slideshow-caption">
                            Fotograma · {STILLS[idx].titulo} · {STILLS[idx].anio}
                        </span>
                        <span>
                            {String(idx + 1).padStart(2, "0")} / {String(STILLS.length).padStart(2, "0")}
                        </span>
                    </div>
                    <div className="pointer-events-none absolute -left-4 top-8 hidden h-24 w-px bg-[#6B1D2F] lg:block" />
                </motion.div>
            </div>

            {/* Indicador de scroll */}
            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                onClick={() => scrollTo("#peliculas")}
                className="mx-auto mt-16 flex flex-col items-center gap-2 font-mono-jf text-[10px] uppercase tracking-[0.3em] text-[#736E65]"
                data-testid="hero-scroll-indicator"
                aria-label="Bajar al catálogo"
            >
                Desliza
                <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
                    <ArrowDown size={14} />
                </motion.span>
            </motion.button>
        </section>
    );
}
